import { Component } from '@theme/component';

/**
 * <delivery-estimate> — "Get it by Sun, 5 Oct – Tue, 7 Oct · Order within
 * 3 h 12 m" (blocks/custom-delivery-estimate.liquid).
 *
 * The server renders a correct, date-free fallback ("Delivery in 1–3 days");
 * this class swaps in real dates, so a cached page never shows a stale date.
 * All maths happens in the STORE's time zone (data-tz, e.g. Asia/Qatar), not
 * the shopper's device zone:
 *   • orders before the cut-off hour count today as dispatch day, later
 *     orders count from the next working day
 *   • closed weekdays (data-closed, 0 = Sun … 6 = Sat; Friday in Qatar) are
 *     never delivery or dispatch days
 * The countdown refreshes every minute and the dates roll over at cut-off.
 *
 * Markup contract: data-min, data-max (working days), data-cutoff (hour 0–23),
 * data-closed ("5" or "5,6"), data-tz, data-locale,
 * data-label-range / data-label-single ("Get it by [date]"),
 * data-label-countdown ("Order within [time]");
 * refs: dates, countdown.
 *
 * @extends {Component<{ dates: HTMLElement, countdown?: HTMLElement }>}
 */
class DeliveryEstimate extends Component {
  requiredRefs = ['dates'];

  /** @type {ReturnType<typeof setInterval> | undefined} */
  #timer;

  connectedCallback() {
    super.connectedCallback();
    this.#render();
    this.#timer = setInterval(() => this.#render(), 60_000);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    clearInterval(this.#timer);
  }

  get #tz() {
    return this.dataset.tz || 'Asia/Qatar';
  }

  get #closed() {
    return (this.dataset.closed ?? '')
      .split(',')
      .filter(Boolean)
      .map(Number);
  }

  /**
   * Wall-clock parts of `date` in the store time zone.
   * @param {Date} date
   */
  #parts(date) {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: this.#tz,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
      weekday: 'short',
    }).formatToParts(date);
    /** @param {string} type */
    const get = (type) => parts.find((p) => p.type === type)?.value ?? '';
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
    return {
      year: Number(get('year')),
      month: Number(get('month')),
      day: Number(get('day')),
      hour: Number(get('hour')),
      minute: Number(get('minute')),
      weekday: weekdays.indexOf(get('weekday')),
    };
  }

  #render() {
    const now = new Date();
    const local = this.#parts(now);
    const cutoff = Number(this.dataset.cutoff ?? 14);
    const closed = this.#closed;

    // A calendar date in the store zone, kept as UTC midnight for safe day maths
    let day = new Date(Date.UTC(local.year, local.month - 1, local.day));
    const isClosed = (/** @type {Date} */ d) => closed.includes(d.getUTCDay());
    const nextDay = (/** @type {Date} */ d) => new Date(d.getTime() + 86_400_000);

    // Dispatch day: today if open and before cut-off, else the next open day
    const beforeCutoff = local.hour < cutoff;
    if (!beforeCutoff || isClosed(day)) {
      day = nextDay(day);
      while (isClosed(day)) day = nextDay(day);
    }

    /** @param {number} workingDays */
    const addWorkingDays = (workingDays) => {
      let d = day;
      let added = 0;
      while (added < workingDays) {
        d = nextDay(d);
        if (!isClosed(d)) added += 1;
      }
      return d;
    };

    const min = Number(this.dataset.min ?? 1);
    const max = Number(this.dataset.max ?? 3);
    const first = addWorkingDays(min);
    const last = addWorkingDays(max);

    const format = new Intl.DateTimeFormat(this.dataset.locale || undefined, {
      weekday: 'short',
      day: 'numeric',
      month: 'short',
      timeZone: 'UTC',
    });
    const dates =
      first.getTime() === last.getTime() ? format.format(first) : `${format.format(first)} – ${format.format(last)}`;
    const label = (first.getTime() === last.getTime() ? this.dataset.labelSingle : this.dataset.labelRange) ?? '[date]';
    this.refs.dates.innerHTML = label.replace('[date]', `<strong>${dates}</strong>`);

    // Countdown to today's cut-off (only while it is still ahead)
    const { countdown } = this.refs;
    if (!countdown) return;
    const todayOpen = !isClosed(new Date(Date.UTC(local.year, local.month - 1, local.day)));
    if (beforeCutoff && todayOpen) {
      const minutesLeft = (cutoff - local.hour) * 60 - local.minute;
      const h = Math.floor(minutesLeft / 60);
      const m = minutesLeft % 60;
      const time = h > 0 ? `${h} h ${m} m` : `${m} m`;
      countdown.innerHTML = (this.dataset.labelCountdown ?? '[time]').replace('[time]', `<strong>${time}</strong>`);
      countdown.hidden = false;
    } else {
      countdown.hidden = true;
    }
  }
}

if (!customElements.get('delivery-estimate')) {
  customElements.define('delivery-estimate', DeliveryEstimate);
}
